import React from "react"
import { SectionTitle, InfoDisplay, GridRow, GridItem, StatusBadge, PaymentTypeIndicator, DataTable } from "../components"
import { cardStyle } from "../styles/commonStyles"
import { formatThaiBuddhistDate, formatRelative } from "../../../libs/dateUtils"
import { calculatePlanStatus, snakeToTitleCase } from "../utils/helpers"
import { TYPOGRAPHY } from "../constants/theme"

const InfoTab = ({
	customer,
	paymentMethods,
	scheduleList,
	loading,
	deleted,
	onDeleteCustomer,
	onDeleteMember,
	onDebugLogin,
	onSubscribe,
	onUpdateExpiredPlan,
}) => {
	if (deleted) {
		return (
			<div className="card-style" style={cardStyle}>
				<div style={{ padding: 20, color: "#d32f2f", fontWeight: 600 }}>บัญชีนี้ถูกลบแล้ว</div>
			</div>
		)
	}

	if (loading || !customer) {
		return (
			<div className="card-style" style={cardStyle}>
				<SectionTitle title="ข้อมูลลูกค้าหนังสือพิมพ์" />
				{loading ? (
					<div style={{ padding: 20, color: "#888", textAlign: "center" }}>กำลังโหลด...</div>
				) : (
					<div
						style={{
							color: "#444",
							background: "#f7f7f7",
							borderRadius: 4,
							padding: "24px 18px",
							textAlign: "center",
						}}
					>
						<span>ไม่พบข้อมูล</span>
					</div>
				)}
			</div>
		)
	}

	const renderContactInfo = () => {
		const memberInfo = customer.memberInfo
		if (!memberInfo) return <span>-</span>

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
				{/* Google Account */}
				{memberInfo.google && (
					<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<LoginIcon type="google" />
						<span style={{ fontWeight: 500 }}>{memberInfo.google.email || memberInfo.google.name || "Google Account"}</span>
						{/* <StatusBadge status="active" customText="เชื่อมโยงแล้ว" /> */}
						{memberInfo.google.name && memberInfo.google.email !== memberInfo.google.name && (
							<span style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: "#888" }}>({memberInfo.google.name})</span>
						)}
					</div>
				)}

				{/* Facebook Account */}
				{memberInfo.facebook && (
					<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<LoginIcon type="facebook" />
						<span style={{ fontWeight: 500 }}>{memberInfo.facebook.email || memberInfo.facebook.name || "Facebook Account"}</span>
						{/* <StatusBadge status="active" customText="เชื่อมโยงแล้ว" /> */}
						{memberInfo.facebook.name && memberInfo.facebook.email !== memberInfo.facebook.name && (
							<span style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: "#888" }}>({memberInfo.facebook.name})</span>
						)}
					</div>
				)}

				{/* Line Account */}
				{memberInfo.line && (
					<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<LoginIcon type="line" />
						<span style={{ fontWeight: 500 }}>{memberInfo.line.email || memberInfo.line.display_name || memberInfo.line.name || "Line Account"}</span>
						{/* <StatusBadge status="active" customText="เชื่อมโยงแล้ว" /> */}
						{(memberInfo.line.display_name || memberInfo.line.name) && memberInfo.line.email !== (memberInfo.line.display_name || memberInfo.line.name) && (
							<span style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: "#888" }}>({memberInfo.line.display_name || memberInfo.line.name})</span>
						)}
					</div>
				)}

				{/* Primary Email - Verified and Has Password (Highest Priority) */}
				{memberInfo.email && memberInfo.verified_email && memberInfo.hasPassword && (
					<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<LoginIcon type="email" />
						<span style={{ fontWeight: 500 }}>{memberInfo.email}</span>
						<StatusBadge status="active" customText="ยืนยันแล้ว" />
						<span style={{ color: "#ddd", fontSize: "14px", margin: "0 4px" }}>|</span>
						<StatusBadge status="active" customText="มีรหัสผ่าน" />
					</div>
				)}

				{/* Phone Number - Verified First */}
				{memberInfo.msisdn && memberInfo.msisdnVerified && (
					<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<LoginIcon type="telno" />
						<span style={{ fontWeight: 500 }}>{memberInfo.msisdn}</span>
						<StatusBadge status="active" customText="ยืนยันแล้ว" />
					</div>
				)}

				{/* Primary Email - Verified but No Password (Lower Priority) */}
				{memberInfo.email && memberInfo.verified_email && !memberInfo.hasPassword && (
					<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<LoginIcon type="email" />
						<span style={{ fontWeight: 500 }}>{memberInfo.email}</span>
						<StatusBadge status="active" customText="ยืนยันแล้ว" />
						<span style={{ color: "#ddd", fontSize: "14px", margin: "0 4px" }}>|</span>
						<StatusBadge status="inactive" customText="ไม่มีรหัสผ่าน" />
					</div>
				)}

				{/* Primary Email - Not Verified */}
				{memberInfo.email && !memberInfo.verified_email && (
					<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<LoginIcon type="email" />
						<span style={{ fontWeight: 500 }}>{memberInfo.email}</span>
						<StatusBadge status="inactive" customText="ยังไม่ยืนยัน" />
						<span style={{ color: "#ddd", fontSize: "14px", margin: "0 4px" }}>|</span>
						{memberInfo.hasPassword ? (
							<StatusBadge status="active" customText="มีรหัสผ่าน" />
						) : (
							<StatusBadge status="inactive" customText="ไม่มีรหัสผ่าน" />
						)}
					</div>
				)}

				{/* Phone Number - Not Verified */}
				{memberInfo.msisdn && !memberInfo.msisdnVerified && (
					<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<LoginIcon type="telno" />
						<span style={{ fontWeight: 500 }}>{memberInfo.msisdn}</span>
						<StatusBadge status="inactive" customText="ยังไม่ยืนยัน" />
					</div>
				)}
			</div>
		)
	}

	const renderPlanInfo = () => {
		if (!customer.plans || customer.plans.length === 0) return "-"

		// const activePlans = customer.plans
		// 	.filter((plan) => {
		// 		const now = new Date()
		// 		return now <= new Date(plan.expiredAt)
		// 	})
		// 	.reverse()

		// const expiredPlans = customer.latestPlan && new Date(customer.expiredAt) < new Date() ? [customer.latestPlan] : []

		// const allPlans = [...activePlans, ...expiredPlans]

		// return (
		// 	<>
		// 		{allPlans.map((plan, idx) => {
		// 			const status = calculatePlanStatus(plan)

		// 			return (
		// 				<div key={idx} style={{ marginBottom: 6 }}>
		// 					<b>{plan.name}</b> <StatusBadge status={status} variant="simple" />
		// 					<br />
		// 					{`เริ่มวันที่: ${formatThaiBuddhistDate(plan.startAt)}`}
		// 					<br />
		// 					{`หมดอายุวันที่: ${formatThaiBuddhistDate(plan.expiredAt)} (${formatRelative(plan.expiredAt)})`}
		// 					<br />
		// 					<span style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: "#888" }}>
		// 						ประเภท: {plan.type} | ราคา: {plan.price?.toLocaleString()} ฿
		// 					</span>
		// 				</div>
		// 			)
		// 		})}
		// 	</>
		// )

		if (!customer.latestPlan) return "-"
		const plan = customer.latestPlan
		const status = calculatePlanStatus(plan)
		return (
			<div key={"latest-plan"} style={{ marginBottom: 6 }}>
				<b>{plan.name}</b> <StatusBadge status={status} variant="simple" />
				<br />
				{`เริ่มวันที่: ${formatThaiBuddhistDate(customer.startAt)}`}
				<br />
				{`หมดอายุวันที่: ${formatThaiBuddhistDate(customer.expiredAt)} (${formatRelative(customer.expiredAt)})`}
				<br />
				<span style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: "#888" }}>
					ประเภท: {plan.type} | ราคา: {plan.price?.toLocaleString()} ฿
				</span>
			</div>
		)
	}

	const renderNextRecurring = () => {
		if (!customer.isRecurring) return null

		if (!scheduleList.description && !scheduleList.nextOccurrencesOn) {
			return <div>-</div>
		}

		if (scheduleList.status !== "running") {
			return <div>-</div>
		}

		return (
			<div>
				{scheduleList.description && (
					<div>
						{scheduleList.description}
						{scheduleList.scheduleId && (
							<span
								style={{
									fontSize: TYPOGRAPHY.fontSize.xs,
									color: "#6c757d",
									backgroundColor: "#e9ecef",
									padding: "2px 6px",
									borderRadius: "4px",
									fontFamily: "monospace",
									marginLeft: 8,
								}}
							>
								#{scheduleList.scheduleId}
							</span>
						)}
					</div>
				)}
				{scheduleList.nextOccurrencesOn && Array.isArray(scheduleList.nextOccurrencesOn) && scheduleList.nextOccurrencesOn.length > 0 && (
					<div style={{ marginTop: 8 }}>
						<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
							{scheduleList.nextOccurrencesOn.map((date, index) => (
								<div key={index} style={{ display: "flex", alignItems: "center", gap: 8 }}>
									<span
										style={{
											width: 6,
											height: 6,
											borderRadius: "50%",
											backgroundColor: index === 0 ? "#28a745" : "#6c757d",
											flexShrink: 0,
										}}
									/>
									{formatThaiBuddhistDate(date, "D MMM BB")} ({formatRelative(date)})
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		)
	}

	const renderPaymentMethods = () => {
		if (!paymentMethods || paymentMethods.length === 0) {
			return <DataTable headers={[]} rows={[]} emptyMessage="ไม่พบข้อมูลช่องทางการชำระเงิน" />
		}

		const headers = ["ประเภท", "ข้อมูลบัตร", "บริการที่ผูกกับบัตร", "ธนาคาร", "วันหมดอายุ", "วันที่เพิ่ม"]

		// ช่วยจัดรูปแบบปี 2 หลัก → 4 หลัก (เช่น 27 => 2027)
		const normalizeYear = function (yy) {
			if (yy == null) return null
			var n = Number(yy)
			if (!isFinite(n)) return null
			return n < 100 ? 2000 + n : n
		}

		// เดือนหมดอายุ <= เดือนปัจจุบัน (ปีเดียวกัน) หรือ ปีน้อยกว่าปัจจุบัน
		const isExpiredOrThisMonth = function (mm, yy) {
			if (mm == null || yy == null) return false
			var month = Number(mm)
			var year = normalizeYear(yy)
			if (!isFinite(month) || !isFinite(year)) return false

			var now = new Date()
			var curY = now.getFullYear()
			var curM = now.getMonth() + 1 // JS: 0-11 → 1-12

			if (year < curY) return true
			if (year === curY && month <= curM) return true
			return false
		}

		const rows = paymentMethods.map(function (method) {
			// ===== ประเภท =====
			var typeLabel = "-"
			if (method && method.type === "card") {
				var isDebit = method.card && method.card.financing === "debit"
				typeLabel = isDebit ? "บัตรเดบิต" : "บัตรเครดิต"
			} else if (method && method.type) {
				typeLabel = snakeToTitleCase(method.type)
			}

			var typeNode = (
				<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
					<span>{typeLabel}</span>
					{method.isDefault && <StatusBadge variant="simple" status="info" customText="บัตรหลัก" />}
				</div>
			)

			// ===== ข้อมูลบัตร =====
			var cardInfoNode = <span style={{ color: "#6c757d" }}>-</span>
			if (method && method.type === "card" && method.card) {
				var last4 = method.card && method.card.last_digits
				if (!last4 && method.card && method.card.number && typeof method.card.number === "string") {
					last4 = method.card.number.slice(-4)
				}
				cardInfoNode = (
					<div>
						{(method.card && method.card.brand) || ""} ****{last4 || ""}
						<div style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: "#6c757d" }}>{(method.card && method.card.name) || ""}</div>
					</div>
				)
			}

			// ===== บริการที่ผูกกับบัตร =====
			var productNode = <span>-</span>
			if (method && method.type === "card" && method.card && method.products) {
				productNode = (
					<div>
						{method.products.map((product, index) => (
							<div key={index}>{product}</div>
						))}
					</div>
				)
			}

			// ===== ธนาคาร =====
			var bankNode = <span>-</span>
			if (method && method.type === "card" && method.card && method.card.bank) {
				bankNode = <div>{method.card.bank}</div>
			}

			// ===== วันหมดอายุ (+ ป้ายหมดอายุ) =====
			var expiryNode = <span>-</span>
			if (method && method.type === "card" && method.card) {
				var mm = method.card.expirationMonth
				var yy = method.card.expirationYear
				if (mm != null && yy != null) {
					var mmStr = String(mm).length === 1 ? "0" + String(mm) : String(mm)
					var showExpired = isExpiredOrThisMonth(mm, yy)
					expiryNode = (
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<span>
								{mmStr}/{normalizeYear(yy)}
							</span>
							{showExpired && <StatusBadge status="card_expired" customText="(หมดอายุ)" />}
						</div>
					)
				}
			}

			// ===== วันที่เพิ่ม =====
			var createdNode = <span>-</span>
			if (method && method.createdAt) {
				createdNode = <div>{formatThaiBuddhistDate(method.createdAt)}</div>
			}

			return [typeNode, cardInfoNode, productNode, bankNode, expiryNode, createdNode]
		})

		return <DataTable headers={headers} rows={rows} emptyMessage="ไม่พบข้อมูลช่องทางการชำระเงิน" />
	}

	return (
		<div className="card-style" style={cardStyle}>
			{/* E-Newspaper Section */}
			<SectionTitle
				title="ข้อมูลลูกค้าหนังสือพิมพ์"
				onDelete={onDeleteCustomer}
				onSubscribe={onSubscribe}
				onUpdateExpiredPlan={onUpdateExpiredPlan}
				showDeletePermission="delete_enewspaper_customer"
			/>

			<GridRow>
				<GridItem>
					<InfoDisplay label="รหัสสมาชิกหนังสือพิมพ์" value={<b>{customer.id}</b>} />
					<InfoDisplay
						label="ช่องทางการชำระเงินปัจจุบัน"
						value={
							customer.currentPaymentMethod && (
								<div style={{ marginBottom: 6 }}>
									{customer.currentPaymentMethod.type === "card" ? (
										<>
											<b>{customer.currentPaymentMethod.card.financing} Card</b> ({customer.currentPaymentMethod.card.brand} ****
											{customer.currentPaymentMethod.card.number})<br />
											<span style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: "#888" }}>
												{customer.currentPaymentMethod.card.name} | {customer.currentPaymentMethod.card.bank} |{" "}
												{customer.currentPaymentMethod.card.expirationMonth}/{customer.currentPaymentMethod.card.expirationYear}
											</span>
										</>
									) : (
										<>{snakeToTitleCase(customer.currentPaymentMethod.type)}</>
									)}
								</div>
							)
						}
					/>
					<InfoDisplay
						label="ทดลองใช้งานฟรี"
						value={
							customer.freeTrial?.isUsed
								? `เริ่มวันที่: ${formatThaiBuddhistDate(customer.freeTrial.startAt)}\nหมดอายุวันที่: ${formatThaiBuddhistDate(customer.freeTrial.expiredAt)}`
								: "-"
						}
					/>

					<InfoDisplay
						label="วันที่ยกเลิก"
						note="(กรณีลูกค้าเคยกดยกเลิก)"
						value={<>{customer.cancelledAt ? formatThaiBuddhistDate(customer.cancelledAt) : "-"}</>}
					/>

					<InfoDisplay
						label="สถานะปัจจุบัน"
						value={
							<>
								<StatusBadge status={customer.enewspaperStatus} />
							</>
						}
					/>

					{/* <InfoDisplay label="วันที่หมดอายุ" value={formatThaiBuddhistDate(customer.expiredAt) || "-"} /> */}
				</GridItem>
				<GridItem>
					<InfoDisplay label="แพ็คเกจปัจจุบัน" value={renderPlanInfo()} />
					<InfoDisplay
						label="ประเภทการชำระเงิน"
						value={<PaymentTypeIndicator isRecurring={customer.isRecurring} planType={customer.latestPlan?.type} schedule={scheduleList} size={20} />}
					/>

					<InfoDisplay label="ระยะเวลาผ่อนผัน" note="(เฉพาะลูกค้าตัดอัตโนมัติเท่านั้น)" value={formatThaiBuddhistDate(customer.gracePeriodEnd) || "-"} />
					{customer.isRecurring && <InfoDisplay label="ตัดอัตโนมัติรอบถัดไป" note="(เริ่มเวลาประมาณ 8.30 น.)" value={renderNextRecurring()} />}
				</GridItem>
			</GridRow>

			<GridRow style={{ marginTop: 24 }} />

			{/* Login Information Section */}
			<SectionTitle
				title="ข้อมูลเข้าสู่ระบบ (Thairath Member)"
				onDelete={onDeleteMember}
				onDebugLogin={onDebugLogin}
				showDeletePermission="delete_enewspaper_customer"
				showDebugPermission="debug_login"
			/>

			<GridRow>
				<GridItem>
					<InfoDisplay label="รหัสสมาชิกไทยรัฐ" value={customer.memberInfo?._id} />
					<InfoDisplay label="ชื่อ / นาม" value={customer.memberInfo?.display_name || customer.memberInfo?.name} />
					<InfoDisplay label="ช่องทางการเข้าสู่ระบบ" value={renderContactInfo()} />
				</GridItem>
				<GridItem>
					<InfoDisplay label="วันที่ลงทะเบียน" value={formatThaiBuddhistDate(customer.memberInfo?.create_time)} />
					<InfoDisplay label="ลงทะเบียนเว็บไซต์" value={customer.memberInfo?.register_client?.name || customer.memberInfo?.utm?.source || "-"} />
					<InfoDisplay label="ลงทะเบียนช่องทาง" value={customer.memberInfo?.register_client?.channel || customer.memberInfo?.utm?.medium || "-"} />
					<InfoDisplay label="ลงทะเบียนแคมเปญ" value={customer.memberInfo?.utm?.campaign || "-"} />
				</GridItem>
			</GridRow>

			<GridRow style={{ marginTop: 24 }} />

			{/* Tax Information Section */}
			<SectionTitle title="ข้อมูลใบกำกับภาษี" />
			<GridRow>
				<GridItem>
					<InfoDisplay label="ชื่อ - นามสกุล" value={customer.customerInfo?.name} />
					<InfoDisplay label="อีเมล์" value={customer.customerInfo?.email || "-"} />
					<InfoDisplay label="เบอร์โทรศัพท์" value={customer.customerInfo?.phoneNumber || "-"} />
					<InfoDisplay label="หมายเลขประจำตัวผู้เสียภาษี" value={customer.customerInfo?.taxId || "-"} />
				</GridItem>
				<GridItem>
					<InfoDisplay label="ที่อยู่" value={customer.customerInfo?.address?.detail} />
					{/* <InfoDisplay label="รายละเอียด" value={customer.customerInfo?.address?.full} /> */}
					<InfoDisplay label="ตำบล" value={customer.customerInfo?.address?.tambon} />
					<InfoDisplay label="อำเภอ" value={customer.customerInfo?.address?.district} />
					<InfoDisplay label="จังหวัด" value={customer.customerInfo?.address?.province} />
					<InfoDisplay label="รหัสไปรษณีย์" value={customer.customerInfo?.address?.postcode} />
				</GridItem>
			</GridRow>

			<GridRow style={{ marginTop: 24 }} />

			{/* Payment Methods Section */}
			<SectionTitle title="ช่องทางการชำระเงิน" />
			{renderPaymentMethods()}

			<GridRow style={{ marginTop: 24 }} />
		</div>
	)
}

// Login Icon Component
const LoginIcon = ({ type }) => {
	const getIconSrc = (type) => {
		switch (type) {
			case "email":
				return "/icons/icons-gmail-logo.svg"
			case "google":
				return "/icons/icons-google.svg"
			case "facebook":
				return "/icons/icons-facebook.svg"
			case "line":
				return "/icons/icons-line.svg"
			case "telno":
				return "/icons/icons-phone.svg"
			default:
				return null
		}
	}

	const iconSrc = getIconSrc(type)

	if (!iconSrc) return null

	return <img src={iconSrc} alt={type} width="30" height="30" style={{ display: "inline-block", verticalAlign: "middle" }} />
}

export default InfoTab
